import { useMemo, useState } from 'react';
import { Gift, Loader2, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { postBattleEvent } from '../lib/battle-client';

type BattleGiftPanelProps = {
  roomId: string;
  targetUserId?: string;
  accessToken?: string | null;
  disabled?: boolean;
  onGiftSent?: () => void;
};

const GIFT_OPTIONS: Array<{ label: string; amount: number; points: number }> = [
  { label: 'Rose', amount: 10, points: 100 },
  { label: 'Crown', amount: 25, points: 250 },
  { label: 'Rocket', amount: 50, points: 500 },
];

export function BattleGiftPanel({
  roomId,
  targetUserId,
  accessToken,
  disabled = false,
  onGiftSent,
}: BattleGiftPanelProps) {
  const [submittingAmount, setSubmittingAmount] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastGiftId, setLastGiftId] = useState<string | null>(null);

  const canSend = useMemo(() => Boolean(targetUserId) && !disabled, [disabled, targetUserId]);

  const handleSendGift = async (amount: number) => {
    if (!targetUserId || !accessToken || disabled) return;
    setSubmittingAmount(amount);
    setError(null);
    try {
      const idempotencyKey = crypto.randomUUID();
      const result = await postBattleEvent(
        roomId,
        {
          type: 'gift',
          targetUserId,
          idempotencyKey,
          amount,
        },
        accessToken,
      );
      const eventGiftId = (result as any)?.event?.metadata?.giftId;
      setLastGiftId(typeof eventGiftId === 'string' ? eventGiftId : idempotencyKey);
      onGiftSent?.();
    } catch (e: any) {
      setError(e?.message || 'Gift failed');
    } finally {
      setSubmittingAmount(null);
    }
  };

  return (
    <Card className="p-4 border-violet-200/60 dark:border-violet-900/50 bg-white/90 dark:bg-neutral-950/70">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 flex items-center gap-2">
          <Gift className="w-4 h-4 text-violet-500" />
          Battle Gifts
        </h3>
        <Badge variant="secondary" className="text-[11px]">Server-authoritative</Badge>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {GIFT_OPTIONS.map((gift) => (
          <Button
            key={gift.label}
            type="button"
            variant="outline"
            className="justify-between"
            disabled={!canSend || submittingAmount !== null}
            onClick={() => void handleSendGift(gift.amount)}
          >
            <span className="flex items-center gap-2">
              {submittingAmount === gift.amount ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 text-amber-500" />
              )}
              {gift.label}
            </span>
            <span className="text-xs text-neutral-500">+{gift.points}</span>
          </Button>
        ))}
      </div>
      {!canSend && (
        <p className="mt-3 text-xs text-neutral-500">
          Gifts unlock once both battlers join the room.
        </p>
      )}
      {error && <p className="mt-3 text-xs text-red-500">{error}</p>}
      {lastGiftId && !error && (
        <p className="mt-3 text-xs text-emerald-600 dark:text-emerald-400">
          Gift sent. Receipt: {lastGiftId.slice(0, 8)}...
        </p>
      )}
    </Card>
  );
}
